import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { useAppDispatch, useAppSelector } from 'redux/store';
import { getParameterTypes } from '../../../../../../../redux/slices/lists/parameterTypesSlice';
import { DetailCard } from 'components/details/DetailCard';
import { DetailList } from 'components/details/DetailList';
import { EditBoolean } from 'components/forms/EditBoolean';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import type { EditableParameter } from '../../edit/EditableParameter';
import { ParameterDefaultExpression } from './ParameterDefaultExpression';

const INOUT_OPTIONS: EditSelectOptionList<string> = [
  { key: 'in', value: 'in', label: 'In' },
  { key: 'out', value: 'out', label: 'Out' },
  { key: 'inout', value: 'inout', label: 'Inout' },
];

interface CardProps {
  readonly parameter: EditableParameter;
  readonly typeOptions: EditSelectOptionList<string>;
  readonly index: number;
  readonly total: number;
  readonly dragHandle: React.ReactNode;
  readonly reorder: (fromIndex: number, toIndex: number) => void;
}

// Its own observer: DetailList renders each item inside a drag render-prop, so the field reads must be tracked here.
const ParameterCard = observer(({ parameter, typeOptions, index, total, dragHandle, reorder }: CardProps) => (
  <DetailCard
    index={index + 1}
    title="Parameter"
    subtitle={parameter.summary}
    collapsible
    dragHandle={dragHandle}
    onMoveUp={index > 0 ? () => reorder(index, index - 1) : undefined}
    onMoveDown={index < total - 1 ? () => reorder(index, index + 1) : undefined}
    onRemove={parameter.delete}
    removeLabel="Remove parameter"
  >
    <div className="d-flex flex-column gap-2">
      <div className="d-flex gap-3">
        <div style={{ flex: 3, minWidth: 0 }}>
          <EditString label="Name" value={parameter.name} onChange={(v) => (parameter.name = v)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <EditSelect label="Type" value={parameter.parameterType} onChange={(v) => (parameter.parameterType = v)} options={typeOptions} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <EditSelect label="Direction" value={parameter.inout} onChange={(v) => (parameter.inout = v)} options={INOUT_OPTIONS} />
        </div>
      </div>
      <EditString label="Description" value={parameter.description} onChange={(v) => (parameter.description = v)} />
      <ParameterDefaultExpression parameter={parameter} />
      <div className="d-flex gap-4">
        <EditBoolean label="Runtime" value={parameter.runtime} onChange={(v) => (parameter.runtime = v)} />
        <EditBoolean label="Mandatory" value={parameter.mandatory} onChange={(v) => (parameter.mandatory = v)} />
      </div>
    </div>
  </DetailCard>
));

/**
 * The Parameters tab: an ordered, drag-reorderable list of parameter cards over the definition's `parameters` Aggregate
 * (add / remove / reorder / edit). Array position is the persisted order.
 */
export const ParametersTab = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => {
  const dispatch = useAppDispatch();
  const { parameter_types } = useAppSelector((state) => state.listOfParameterTypes);

  useEffect(() => {
    if (!parameter_types.length) dispatch(getParameterTypes());
  }, [dispatch, parameter_types.length]);

  const typeOptions: EditSelectOptionList<string> = parameter_types.map((pt) => ({
    key: pt.parameter_type,
    value: pt.parameter_type,
    label: pt.parameter_type,
  }));
  const parameters = entity.parameters;

  return (
    <DetailList
      items={parameters}
      getKey={(parameter) => parameter.key}
      onReorder={entity.reorderParameters}
      onAdd={entity.createParameter}
      addLabel="Add parameter"
      renderItem={(parameter, index, dragHandle) => (
        <ParameterCard
          parameter={parameter}
          typeOptions={typeOptions}
          index={index}
          total={parameters.length}
          dragHandle={dragHandle}
          reorder={entity.reorderParameters}
        />
      )}
    />
  );
});
